import { create } from '@bufbuild/protobuf';
import { timestampDate } from '@bufbuild/protobuf/wkt';
import { type Account } from '@qdrant/qdrant-cloud-public-api/account/v1';
import { OnboardingStatus, UserProfileSchema, type User } from '@qdrant/qdrant-cloud-public-api/iam/v1';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useRouteContext, useSearch } from '@tanstack/react-router';
import { memoize } from 'micro-memoize';
import { useMemo } from 'react';
import { authQueryKey } from '@/api/query-keys';
import { type AuthDto } from '@/api/services/auth';
import { iamService } from '@/api/services/iam';
import { useSafeQuery } from '@/hooks/use-safe-query';
import {
  type RouteContextQuery,
  type RouteContextSelect,
  type RouteId,
  type RouteSearchSelect,
} from '@/router/route-hooks.types';
import { getAccountsWithOwnership } from '@/utils/account-utils';

const ROUTE_ID = '/_authenticated' satisfies RouteId;

export const useRouteContextQuery = <TSelected extends RouteContextQuery>(
  select: RouteContextSelect<typeof ROUTE_ID, TSelected>,
) => useRouteContext({ from: ROUTE_ID, select });

export const useRouteSearch = <TSelected, TId = typeof ROUTE_ID>(select: RouteSearchSelect<TId, TSelected>) =>
  useSearch({ from: ROUTE_ID, select });

/**
 * Returns a cleaned-up version of the authenticated user data, ensuring that a User object is always returned.
 */
export const useAuthenticatedUser = (): User => {
  const { data: user } = useSuspenseQuery(useRouteContextQuery((s) => s.getAuthenticatedUserQuery));
  if (!user) {
    throw new Error('Authenticated user data is not available');
  }
  return user;
};

/**
 * Returns the auth claims for the authenticated user.
 *
 * Important: It presumes data to always be present in cache as it is being loaded within a route before this hook is called,
 * otherwise suspense boundaries will be triggered.
 */
export const useAuthenticatedUserClaims = () => {
  const { data } = useSuspenseQuery({ queryKey: authQueryKey, select: ({ claims }: AuthDto) => claims });
  return data;
};

/**
 * Returns the current user profile details from the API, backed by the authenticated JWT claims.
 */
export const useUserProfile = () => {
  const userClaims = useAuthenticatedUserClaims();
  const queryOptions = useRouteContextQuery((s) => s.getUserProfileQuery);
  // Not-found (no profile yet) is already filtered from Sentry by getUserProfileQuery's meta
  const { data } = useQuery(queryOptions);

  return useMemo(() => {
    const userProfile = data ?? create(UserProfileSchema);
    const fullName = [userProfile.givenName, userProfile.familyName]
      .filter(Boolean)
      .map((part) => part.trim())
      .join(' ');

    return { ...userProfile, name: fullName || userClaims.name };
  }, [data, userClaims.name]);
};

/**
 * Whether the user has to finish onboarding first: no account yet, and onboarding not completed.
 * Non-suspending, so the dashboard renders while the data loads: `false` until both are known.
 */
export const useEnsureOnboardingStatus = (): boolean => {
  const { data: accounts } = useQuery(useRouteContextQuery((s) => s.listAccountsQuery));
  const { data: user } = useQuery(useRouteContextQuery((s) => s.getAuthenticatedUserQuery));

  return accounts?.length === 0 && user !== undefined && user.onboardingStatus !== OnboardingStatus.COMPLETED;
};

/**
 * Returns all accounts for the authenticated user, without any specific order. For a sorted list of accounts, use `useSortedUserAccounts` instead.
 */
export const useUserAccounts = (): Account[] => {
  const { data: accounts } = useSuspenseQuery(useRouteContextQuery((s) => s.listAccountsQuery));
  return accounts;
};

/**
 * Returns all accounts where the authenticated user is the owner.
 */
export const useUserOwnAccounts = (): Account[] => {
  const user = useAuthenticatedUser();
  const { data: accounts } = useSuspenseQuery({
    ...useRouteContextQuery((s) => s.listAccountsQuery),
    select: ({ items }) => getAccountsWithOwnership(items, user),
  });
  return accounts;
};

/**
 * Returns the external owner ID for the authenticated user, derived from their owned accounts.
 */
export const useUserExternalId = (): string => {
  const firstOwnerAccount = useUserOwnAccounts().at(0);
  if (!firstOwnerAccount) {
    throw new Error('No owner account found for the current user');
  }
  return firstOwnerAccount.externalOwnerId;
};

/**
 * Returns all accounts owned by the authenticated user, sorted by creation date (oldest first),
 * with owned accounts prioritized over shared accounts, and the default account (if set) prioritized at the top.
 */
export const useSortedUserAccounts = (): Account[] => {
  const user = useAuthenticatedUser();
  const accounts = useUserAccounts();

  return sortedUserAccounts(accounts, user);
};

/**
 * Memoized function to use in useSortedUserAccounts()
 */
export const sortedUserAccounts = memoize((accounts: Account[], user: User) =>
  accounts
    .toSorted(({ createdAt: a }, { createdAt: b }) => (timestampDate(a!) < timestampDate(b!) ? -1 : 1))
    .toSorted((a, b) => (user.email === a.ownerEmail && user.email !== b.ownerEmail ? -1 : 1))
    .toSorted((a, b) => (a.id === user.defaultAccountId && b.id !== user.defaultAccountId ? -1 : 1)),
);

export const useEffectivePermissions = (): Set<string> => {
  const [listEffectivePermissionsQuery, listAccountsQuery, getAuthenticatedUserQuery] = useRouteContext({
    strict: false,
    select: (s) => [s.listEffectivePermissionsQuery, s.listAccountsQuery, s.getAuthenticatedUserQuery] as const,
  });

  /**
   * Consider a use-case where you directly jump to `<url>/invitations`. In this case, the route will have no Account ID since `/invitations`
   * is a user-related route. Now, since there is no account ID, the `_authenticated` route will not be able to provide the
   * `listEffectivePermissionsQuery` because this query mandates an Account ID. Now, that this query options is not avaliable,
   * any route context calls to get this query option will result in null. As a result, the sidebar nav items calls
   * `WithRpcPermissions` which internally requests this query option from the route's context i.e _authenticated route. This will mean
   * the useRpcPermissions's hook call will return nothing about the current permissions thereby returning null when trying to check for
   * it a.k.a falsy value. As a result, none of the Account related nav items will be visible. It is also possible to await the `listAccount`
   * and then determine this however, it would mean awaiting listAccount query before the page (this affects all the page
   * and stops it from painting) can Paint which will introduce a delay in rendering the page and is not ideal for
   * user experience nor is it efficient in terms of performance. Hence, it is derived here using the same sorted list
   * `useSelectedAccount()` picks from so that both agree on which Account "no Id in URL" would correspond to.
   *
   */
  const { data: fallbackAccounts } = useSafeQuery(listEffectivePermissionsQuery ? undefined : listAccountsQuery);
  const { data: fallbackUser } = useSafeQuery(listEffectivePermissionsQuery ? undefined : getAuthenticatedUserQuery);
  const fallbackAccountId =
    fallbackAccounts && fallbackUser ? sortedUserAccounts(fallbackAccounts, fallbackUser).at(0)?.id : undefined;

  const { data } = useSafeQuery(
    listEffectivePermissionsQuery ??
      (fallbackAccountId ? iamService.listEffectivePermissionsQuery({ accountId: fallbackAccountId }) : undefined),
  );

  return useMemo(() => new Set(data ? data.map(({ value }) => value) : []), [data]);
};
